import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";

/**
 * Circular crop modal used when a Pro host picks a photo for the buzzer logo.
 * Users can pan + zoom the source image while a circular selection sits over it.
 * On save, the visible circle is exported as a square PNG blob (buzzer is rounded-full,
 * so the square automatically appears as a circle inside the button).
 *
 * Props:
 *  - file: File | null            — the selected image
 *  - onCancel(): void             — user closed the modal
 *  - onCropped(blob: Blob): void  — user confirmed; blob is a PNG containing the crop
 */
const OUTPUT_SIZE = 512; // px — final square size of the exported PNG

const LogoCropModal = ({ file, onCancel, onCropped }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Read the file into a base64 data URL for the cropper
  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.onerror = () => setError("Could not read that image — try another one.");
    reader.readAsDataURL(file);
  }, [file]);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  // Load an image element from a data URL
  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setBusy(true);
    setError(null);
    try {
      const img = await loadImage(imageSrc);

      // Draw the cropped region onto a square canvas
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "rgba(0,0,0,0)";
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      ctx.drawImage(
        img,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE
      );

      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png", 0.92)
      );
      if (!blob) {
        setError("Could not export the crop — try a different image.");
        return;
      }
      onCropped(blob);
    } catch (e) {
      setError("Cropping failed — please try another image.");
    } finally {
      setBusy(false);
    }
  };

  if (!file) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center px-4"
      data-testid="logo-crop-modal"
    >
      <div className="w-full max-w-md bg-[#0F0F14] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h3
              className="text-white text-lg font-bold tracking-wide"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              CROP YOUR LOGO
            </h3>
            <p className="text-[11px] text-[#A1A1AA] mt-0.5">
              Drag to pan · pinch or use the slider to zoom
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-[#A1A1AA] hover:text-white text-2xl leading-none px-2"
            aria-label="Close"
            data-testid="crop-cancel-icon"
          >
            ×
          </button>
        </div>

        {/* Cropper stage */}
        <div className="relative w-full aspect-square bg-black">
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={1}
              maxZoom={4}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
              style={{
                containerStyle: { background: "#000" },
                cropAreaStyle: {
                  border: "3px solid #00F0FF",
                  boxShadow: "0 0 0 9999em rgba(0,0,0,0.55)",
                },
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#A1A1AA] text-sm">
              Loading image…
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-[11px] tracking-widest text-[#A1A1AA] uppercase">
              Zoom
            </label>
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full accent-[#00F0FF] mt-1"
              data-testid="crop-zoom-slider"
            />
          </div>

          {error && (
            <div className="text-[#FF0055] text-xs" data-testid="crop-error">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              onClick={onCancel}
              disabled={busy}
              className="flex-1 px-4 py-2.5 bg-white/10 border border-white/20 text-white text-sm font-semibold rounded-lg hover:bg-white/20 transition-all disabled:opacity-50"
              data-testid="crop-cancel-button"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={busy || !croppedAreaPixels}
              className="flex-1 px-4 py-2.5 bg-[#00F0FF] text-black text-sm font-bold rounded-lg hover:bg-[#33F3FF] transition-all active:scale-95 disabled:opacity-50"
              data-testid="crop-save-button"
            >
              {busy ? "Saving…" : "Use this crop"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoCropModal;
